// Best-effort location capture for clock-in / clock-out.
//
// This never rejects and never throws. An agent refusing the browser prompt,
// a device with no GPS, a slow fix, or a page served over plain HTTP all
// resolve to null -- the punch still goes through, just without coordinates.
// Blocking someone from starting their shift because a satellite was slow
// would be a far worse failure than a missing pin on a report.

const DEFAULT_TIMEOUT = 8000;

export const LOCATION_DENIED = "denied";
export const LOCATION_UNAVAILABLE = "unavailable";

/**
 * Resolves { coords, reason }. `coords` is null when nothing usable came back,
 * and `reason` says why, so the UI can tell "you declined" from "we could not
 * get a fix" rather than silently showing nothing.
 */
export const captureLocation = ({ timeout = DEFAULT_TIMEOUT } = {}) =>
  new Promise((resolve) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      resolve({ coords: null, reason: LOCATION_UNAVAILABLE });
      return;
    }

    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };

    // getCurrentPosition's own timeout is not always honoured (some browsers
    // sit on the permission prompt indefinitely), so back it with our own.
    const guard = setTimeout(
      () => finish({ coords: null, reason: LOCATION_UNAVAILABLE }),
      timeout + 1000
    );

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        clearTimeout(guard);
        finish({
          coords: {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
          },
          reason: null,
        });
      },
      (err) => {
        clearTimeout(guard);
        finish({
          coords: null,
          reason: err?.code === 1 ? LOCATION_DENIED : LOCATION_UNAVAILABLE,
        });
      },
      { enableHighAccuracy: true, timeout, maximumAge: 60000 }
    );
  });

/** A link to the pin on a map, for the admin screens. */
export const mapsUrl = (loc) =>
  loc ? `https://www.google.com/maps/search/?api=1&query=${loc.lat},${loc.lng}` : null;

/** "12.9716, 77.5946 (±18 m)" */
export const formatCoords = (loc) => {
  if (!loc) return "";
  const base = `${loc.lat.toFixed(5)}, ${loc.lng.toFixed(5)}`;
  return loc.accuracy ? `${base} (±${Math.round(loc.accuracy)} m)` : base;
};
